import type { DefaultIconPositions } from "../types";

/**
 * App Registry — single source of truth for window IDs, icon IDs, titles, and
 * labels for the portfolio desktop.
 *
 * Identity only. Behavioral config (dimensions, React components, icon SVGs,
 * dockPriority, browser URLs) lives in desktop-config.tsx. The registry and the
 * config are a matched pair: `assertConfigMatchesRegistry` (in desktop-config)
 * throws at load if they drift, so every entry here must have a matching
 * window + icon in the config, and vice-versa.
 *
 * To add an app: add an entry below. The `as const satisfies` clause fails
 * compilation on duplicate windowId/iconId, and the AppKey/WindowId/IconId
 * unions automatically pick up the new value across the codebase.
 */

type AppDefinition = {
  /** Stable window manager ID. Used by openWindow/focusWindow/etc. */
  windowId: string;
  /** Window title bar text. */
  title: string;
  /** Dock/desktop icon ID. */
  iconId?: string;
  /** Dock caption shown under the icon. */
  label?: string;
};

/* --------------------------------------------------------------------------
   Registry — every window known to the app
   -------------------------------------------------------------------------- */

export const APP_REGISTRY = {
  // --- Portfolio apps (custom content) ---
  about: {
    windowId: "about",
    title: "About Me",
    iconId: "about-icon",
    label: "About Me",
  },
  resume: {
    windowId: "resume",
    title: "Résumé",
    iconId: "resume-icon",
    label: "Résumé",
  },
  experience: {
    windowId: "experience",
    title: "Experience",
    iconId: "experience-icon",
    label: "Experience",
  },
  skills: {
    windowId: "skills",
    title: "Skills",
    iconId: "skills-icon",
    label: "Skills",
  },
  projects: {
    windowId: "projects",
    title: "Projects",
    iconId: "projects-icon",
    label: "Projects",
  },
  contact: {
    windowId: "contact",
    title: "Contact",
    iconId: "contact-icon",
    label: "Contact",
  },
  // --- Core utilities ---
  settings: {
    windowId: "settings",
    title: "Settings",
    iconId: "settings-icon",
    label: "Settings",
  },
  calculator: {
    windowId: "calculator",
    title: "Calculator",
    iconId: "calculator-icon",
    label: "Calculator",
  },
  stopwatch: {
    windowId: "stopwatch",
    title: "Clock",
    iconId: "stopwatch-icon",
    label: "Clock",
  },
  browser: {
    windowId: "browser",
    title: "Browser",
    iconId: "browser-icon",
    label: "Browser",
  },
} as const satisfies Record<string, AppDefinition>;

/* --------------------------------------------------------------------------
   Extra icons — non-app icons (none currently)
   -------------------------------------------------------------------------- */

export const EXTRA_ICON_IDS = {} as const;

/* --------------------------------------------------------------------------
   Derived types & runtime helpers
   -------------------------------------------------------------------------- */

export type AppKey = keyof typeof APP_REGISTRY;
export type AppEntry<K extends AppKey = AppKey> = (typeof APP_REGISTRY)[K];

export type WindowId = (typeof APP_REGISTRY)[AppKey]["windowId"];

type IconIdFromApps = Extract<
  (typeof APP_REGISTRY)[AppKey],
  { iconId: string }
>["iconId"];
export type IconId = IconIdFromApps;

/** All known WindowIds as a frozen array — useful for runtime validation. */
export const WINDOW_IDS: readonly WindowId[] = Object.freeze(
  Object.values(APP_REGISTRY).map((app) => app.windowId)
) as readonly WindowId[];

/** All known IconIds. */
export const ICON_IDS: readonly IconId[] = Object.freeze(
  (Object.values(APP_REGISTRY) as ReadonlyArray<AppDefinition>)
    .map((app) => app.iconId)
    .filter((id): id is IconIdFromApps => Boolean(id))
) as readonly IconId[];

const WINDOW_ID_SET = new Set<string>(WINDOW_IDS);
const ICON_ID_SET = new Set<string>(ICON_IDS);

/** Runtime guard for places that can't get static typing (e.g. deep-link params). */
export function isWindowId(value: unknown): value is WindowId {
  return typeof value === "string" && WINDOW_ID_SET.has(value);
}

export function isIconId(value: unknown): value is IconId {
  return typeof value === "string" && ICON_ID_SET.has(value);
}

/** Look up the registry entry for a given windowId (typed). */
export function getAppByWindowId(windowId: WindowId): AppEntry {
  const entry = Object.values(APP_REGISTRY).find(
    (app) => app.windowId === windowId
  );
  if (!entry) {
    throw new Error(`Unknown windowId: ${windowId}`);
  }
  return entry;
}

/* --------------------------------------------------------------------------
   Exported windowId / iconId constants
   -------------------------------------------------------------------------- */

export const WINDOW_ID = {
  about: APP_REGISTRY.about.windowId,
  resume: APP_REGISTRY.resume.windowId,
  experience: APP_REGISTRY.experience.windowId,
  skills: APP_REGISTRY.skills.windowId,
  projects: APP_REGISTRY.projects.windowId,
  contact: APP_REGISTRY.contact.windowId,
  settings: APP_REGISTRY.settings.windowId,
  calculator: APP_REGISTRY.calculator.windowId,
  stopwatch: APP_REGISTRY.stopwatch.windowId,
  browser: APP_REGISTRY.browser.windowId,
} as const;

export const ICON_ID = {
  about: APP_REGISTRY.about.iconId,
  resume: APP_REGISTRY.resume.iconId,
  experience: APP_REGISTRY.experience.iconId,
  skills: APP_REGISTRY.skills.iconId,
  projects: APP_REGISTRY.projects.iconId,
  contact: APP_REGISTRY.contact.iconId,
  settings: APP_REGISTRY.settings.iconId,
  calculator: APP_REGISTRY.calculator.iconId,
  stopwatch: APP_REGISTRY.stopwatch.iconId,
  browser: APP_REGISTRY.browser.iconId,
} as const;

/** Silence window-config warnings for specific apps (none currently). */
export const WINDOW_CONFIG_WARNING_WHITELIST = [] as const;

/* --------------------------------------------------------------------------
   DEFAULT ICON POSITIONS

   Drives the initial icon layout before the user makes manual customizations
   (also used by "Reset Icon Positions" in Settings). Validated at hook init.

   Desktop: `dockOrder` is the exact left-to-right dock order (no auto-fill);
   any non-pinned icon not listed becomes hidden (Takeoff / search only).
   Mobile: `dock` is up to 4 left-to-right slots; `grids` are sparse positions
   on a 4-col × 5-row grid, pages 0-indexed and sequential.
   -------------------------------------------------------------------------- */

export const DEFAULT_DESKTOP_ICON_POSITIONS: NonNullable<
  DefaultIconPositions["desktop"]
> = {
  dockOrder: [
    ICON_ID.about,
    ICON_ID.resume,
    ICON_ID.experience,
    ICON_ID.skills,
    ICON_ID.projects,
    ICON_ID.contact,
    ICON_ID.settings,
    ICON_ID.calculator,
    ICON_ID.stopwatch,
    ICON_ID.browser,
  ] satisfies IconId[],
};

export const DEFAULT_MOBILE_ICON_POSITIONS: NonNullable<
  DefaultIconPositions["mobile"]
> = {
  // Bottom dock: up to 4 slots.
  dock: [
    ICON_ID.about,
    ICON_ID.projects,
    ICON_ID.contact,
    ICON_ID.browser,
  ] satisfies IconId[],
  // Home-screen grid (4 cols × 5 rows per page).
  grids: {
    [ICON_ID.resume]: { page: 0, row: 0, col: 0 },
    [ICON_ID.experience]: { page: 0, row: 0, col: 1 },
    [ICON_ID.skills]: { page: 0, row: 0, col: 2 },
    [ICON_ID.settings]: { page: 0, row: 0, col: 3 },
    [ICON_ID.calculator]: { page: 0, row: 1, col: 0 },
    [ICON_ID.stopwatch]: { page: 0, row: 1, col: 1 },
  },
};
